import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  ArrowRight,
  BarChart3,
  CalendarClock,
  ClipboardCheck,
  FileCheck,
  ShieldCheck,
  Users,
} from 'lucide-react';

const services = [
  {
    icon: ClipboardCheck,
    title: 'Applicant Onboarding',
    description: 'Application forms, employment history, references, right to work and DBS evidence organised in one readiness workflow.',
  },
  {
    icon: FileCheck,
    title: 'Document Control',
    description: 'Track missing, expired, submitted and approved documents with clearer evidence ownership.',
  },
  {
    icon: ShieldCheck,
    title: 'Training & Checks',
    description: 'Keep mandatory training, induction, DBS and safer recruitment checks visible before applicants become active workers.',
  },
  {
    icon: Users,
    title: 'Care Team Records',
    description: 'Maintain employee, service-user and operational records in a workspace built for care teams.',
  },
  {
    icon: CalendarClock,
    title: 'Shifts & Timesheets',
    description: 'Connect readiness, shifts, attendance and timesheet review so teams can act with better context.',
  },
  {
    icon: BarChart3,
    title: 'Audit Visibility',
    description: 'Give managers a plain view of what is ready, what is blocked and what needs review.',
  },
];

export default function ServicesPage() {
  return (
    <div className="min-h-screen bg-[#F8FAFA]">
      <Header />

      <section className="bg-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-text-primary mb-6">
              CareOps Product
            </h1>
            <p className="text-lg text-text-muted leading-relaxed">
              Invite-only workplace software for care providers that need clearer onboarding, checks, records and operational evidence.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <div key={service.title} className="card-public" data-testid={`service-card-${idx}`}>
                <div className="w-14 h-14 bg-accent rounded-2xl flex items-center justify-center mb-6">
                  <service.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-text-primary mb-3">{service.title}</h3>
                <p className="text-text-muted text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/6129442/pexels-photo-6129442.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Care team reviewing operational records"
                className="rounded-3xl shadow-lg w-full object-cover aspect-[4/3]"
              />
            </div>
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-text-primary mb-6">
                Built around safer onboarding
              </h2>
              <p className="text-lg text-text-muted mb-6">
                The Essential plan keeps enough of the system available for a provider to onboard applicants and complete core readiness checks before work starts.
              </p>
              <p className="text-text-muted mb-8">
                Higher tiers and add-ons can unlock branded documents, client visibility, advanced evidence exports, auditor access and larger operational limits.
              </p>
              <Button asChild className="bg-primary hover:bg-primary-hover text-white rounded-full" data-testid="request-invite-btn">
                <Link to="/contact?type=platform_demo">
                  Request invite
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
            Ready to set up a workplace?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Access is managed by invite while each workplace and subscription is reviewed by the CareOps owner.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-secondary hover:bg-white/90 rounded-full px-8 h-14" data-testid="cta-request-invite-btn">
              <Link to="/contact?type=platform_demo">
                Request Invite
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 h-14" data-testid="cta-workplace-login-btn">
              <Link to="/login">
                Workplace Login
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
